import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Executive Dashboard APIs
export const executiveAPI = {
  getKPIs: () => api.get('/executive/kpis'),
  getAlerts: () => api.get('/executive/alerts'),
  getProjectHealth: () => api.get('/executive/project-health'),
  getFinancial: () => api.get('/executive/financial'),
  getOpsHealth: () => api.get('/executive/ops-health'),
  getMetrics: () => api.get('/executive/metrics'),
};

// Stations APIs
export const stationsAPI = {
  getAll: (params) => api.get('/stations', { params }),
  getById: (id) => api.get(`/stations/${id}`),
  getCitiesSummary: () => api.get('/stations/cities/summary'),
  sendCommand: (id, command) => api.post(`/stations/${id}/command`, { command }),
};

// Tickets APIs
export const ticketsAPI = {
  getAll: (params) => api.get('/tickets', { params }),
  performAction: (id, action, notes) => api.post(`/tickets/${id}/actions`, { action, notes }),
  getSLASummary: () => api.get('/tickets/sla-summary'),
  getAgingAnalysis: () => api.get('/tickets/aging-analysis'),
};

// DSAT APIs
export const dsatAPI = {
  ingestFeedback: (data) => api.post('/dsat/feedback', data),
  getCells: (city) => api.get('/dsat/cells', { params: { city } }),
  getScores: (params) => api.get('/dsat/scores', { params }),
  getTargets: (params) => api.get('/dsat/targets', { params }),
  approveBatch: (target_ids) => api.post('/dsat/targets/approve-batch', { target_ids }),
  getCellBreakdown: (cell_id) => api.get(`/dsat/cell/${cell_id}/breakdown`),
  getKPIs: (city) => api.get('/dsat/kpis', { params: { city } }),
};

// Revenue APIs
export const revenueAPI = {
  getSummary: (start_date, end_date) => api.get('/revenue/summary', { params: { start_date, end_date } }),
  getSessions: (params) => api.get('/revenue/sessions', { params }),
  getInvoices: (params) => api.get('/revenue/invoices', { params }),
  markInvoicePaid: (id) => api.post(`/revenue/invoices/${id}/mark-paid`),
  getDisputes: () => api.get('/revenue/disputes'),
  getToDAnalysis: () => api.get('/revenue/tod-analysis'),
};

// Compliance APIs
export const complianceAPI = {
  getMatrix: () => api.get('/compliance/matrix'),
  getEVYatraStatus: () => api.get('/compliance/ev-yatra'),
  exportEVYatra: () => api.post('/compliance/ev-yatra/export'),
  getCertInStatus: () => api.get('/compliance/cert-in'),
  getRenewalsDue: (days) => api.get('/compliance/renewals-due', { params: { days } }),
};

// SLA APIs
export const slaAPI = {
  getCredits: (params) => api.get('/sla/credits', { params }),
  getCreditsSummary: () => api.get('/sla/credits/summary'),
};

// Audit APIs
export const auditAPI = {
  getEvents: (params) => api.get('/audit/events', { params }),
  getStats: () => api.get('/audit/stats'),
};


// Phase 4: Governance APIs
export const governanceAPI = {
  getAll: (status) => api.get('/dsat/governance', { params: { status } }),
  getKPIs: () => api.get('/dsat/governance/kpis'),
  getCell: (cellId) => api.get(`/dsat/governance/${cellId}`),
  approve: (cellId) => api.post(`/dsat/governance/${cellId}/approve`),
  reject: (cellId, reason) => api.post(`/dsat/governance/${cellId}/reject`, { reason }),
  blocklist: (cellId, days, reason) => api.post(`/dsat/governance/${cellId}/blocklist`, { days, reason }),
  addNote: (cellId, text) => api.post(`/dsat/governance/${cellId}/note`, { text }),
  bulkApprove: (cellIds) => api.post('/dsat/governance/bulk/approve', cellIds),
};

// Phase 4: OCPI APIs
export const ocpiAPI = {
  getPartners: (status) => api.get('/ocpi/partners', { params: { status } }),
  pingPartner: (partnerId) => api.post(`/ocpi/partners/${partnerId}/ping`),
  enablePartner: (partnerId) => api.post(`/ocpi/partners/${partnerId}/enable`),
  pausePartner: (partnerId) => api.post(`/ocpi/partners/${partnerId}/pause`),
  getEnrollments: (status) => api.get('/ocpi/enrollments', { params: { status } }),
  createEnrollment: (data) => api.post('/ocpi/enrollments', data),
  approveEnrollment: (enrollmentId) => api.post(`/ocpi/enrollments/${enrollmentId}/approve`),
  getSettlementSummary: () => api.get('/ocpi/settlement/summary'),
};

// Phase 4: Incidents APIs
export const incidentsAPI = {
  getAll: (params) => api.get('/incidents', { params }),
  getKPIs: () => api.get('/incidents/kpis'),
  getIncident: (id) => api.get(`/incidents/${id}`),
  create: (data) => api.post('/incidents', data),
  acknowledge: (id) => api.post(`/incidents/${id}/acknowledge`),
  close: (id, resolution) => api.post(`/incidents/${id}/close`, { resolution }),
  escalate: (id) => api.post(`/incidents/${id}/escalate`),
};

// Phase 4: ESG APIs
export const esgAPI = {
  getMetrics: () => api.get('/esg/metrics'),
  getSummary: () => api.get('/esg/summary'),
  exportReport: () => api.post('/esg/export'),
};

// Phase 4: Pilot APIs
export const pilotAPI = {
  getAnalytics: () => api.get('/pilot/analytics'),
  getKPIs: () => api.get('/pilot/kpis'),
  getCohort: (params) => api.get('/pilot/cohort', { params }),
  getFunnel: () => api.get('/pilot/funnel'),
  getInsights: () => api.get('/pilot/insights'),
};

// Phase 4: Exports Center APIs (extend existing exports)
export const exportsAPI = {
  getAll: () => api.get('/exports'),
  create: (type, params) => api.post(`/exports?export_type=${type}&expiry_hours=24`, params || {}),
  invalidate: (exportId) => api.post(`/exports/${exportId}/invalidate`, {}),
  getStatus: () => api.get('/exports/status'),
};

// Admin Security Settings APIs
export const securityAPI = {
  getSettings: () => api.get('/admin/security'),
  updateSettings: (settings) => api.put('/admin/security', settings),
  getRateLimits: () => api.get('/admin/security/rate-limits'),
  updateRateLimits: (rateLimits) => api.put('/admin/security/rate-limits', rateLimits),
  getAlertThresholds: () => api.get('/admin/security/alert-thresholds'),
  getFeatureFlags: () => api.get('/admin/security/feature-flags'),
  updateFeatureFlags: (flags) => api.put('/admin/security/feature-flags', flags),
  testSlackAlert: () => api.post('/admin/security/alerts/dry-run'),
};

export default api;
